import React, { ChangeEvent, FC, FocusEvent } from 'react';

import { TextInput } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';

import IconAmex from 'components/icons/amex';
import IconDinersClub from 'components/icons/diners-club';
import IconDiscover from 'components/icons/discover';
import IconJCB from 'components/icons/jcb';
import IconMastercard from 'components/icons/mastercard';
import IconVisa from 'components/icons/visa';
import { PaymentFieldVariations } from 'state/launchdarkly/variations';
import { CardType } from 'state/payment/types';
import { creditCardNumberMask, expiryMask } from 'utils/masks';
import { IPaymentErrors, IPaymentState } from 'utils/payment';

import {
  CardNumberWrapper,
  StyledCvvWrapper,
  StyledExpiryCVVWrapper,
  StyledExpiryWrapper,
} from './styled';

interface IFirstDataCreditCardFormInputs {
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onBlur: (event: FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
  errors: IPaymentErrors;
  paymentValues: IPaymentState;
  supportedCardTypes?: CardType[];
  paymentFieldVariations: PaymentFieldVariations;
}

const CreditCardIcon: FC<{ cardType: string }> = ({ cardType }) =>
  ({
    AMEX: <IconAmex />,
    DISCOVER: <IconDiscover />,
    DINERS_CLUB: <IconDinersClub />,
    JCB: <IconJCB />,
    MASTERCARD: <IconMastercard />,
    VISA: <IconVisa />,
  }[cardType] || null);

const FirstDataCreditCardInputs: FC<IFirstDataCreditCardFormInputs> = ({
  errors,
  onChange,
  onBlur,
  paymentValues,
  paymentFieldVariations,
}) => {
  const { formatMessage } = useIntl();

  return (
    <>
      {paymentFieldVariations.cardNumber && (
        <CardNumberWrapper>
          <TextInput
            aria-label={formatMessage({ id: 'creditCardNumber' })}
            onChange={onChange}
            onBlur={onBlur}
            label={formatMessage({ id: 'creditCardNumber' })}
            value={paymentValues.cardNumber}
            required
            data-testid="cardNumber"
            errorMessage={errors.cardType || errors.cardNumber}
            name="cardNumber"
            autoComplete="cc-number"
            inputMode="numeric"
            mask={creditCardNumberMask}
            guide
            placeholderChar=" "
            icon={
              paymentValues.isCardNumberValid ? (
                <CreditCardIcon cardType={paymentValues.cardType} />
              ) : null
            }
          />
        </CardNumberWrapper>
      )}
      <StyledExpiryCVVWrapper>
        {paymentFieldVariations.expiration && (
          <StyledExpiryWrapper>
            <TextInput
              aria-label={formatMessage({ id: 'cardExpiration' })}
              onChange={onChange}
              onBlur={onBlur}
              label={formatMessage({ id: 'monthYearShort' })}
              value={paymentValues.expiry || ''}
              required
              data-testid="expiry"
              errorMessage={errors.expiry}
              name="expiry"
              autoComplete="cc-exp"
              inputMode="numeric"
              mask={expiryMask}
              guide={false}
            />
          </StyledExpiryWrapper>
        )}
        {paymentFieldVariations.cvv && (
          <StyledCvvWrapper>
            <TextInput
              aria-label={formatMessage({ id: 'cvv' })}
              onChange={onChange}
              onBlur={onBlur}
              label={formatMessage({ id: 'cvv' })}
              value={paymentValues.cvv || ''}
              required
              data-testid="cvv"
              errorMessage={errors.cvv}
              name="cvv"
              autoComplete="cc-csc"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
            />
          </StyledCvvWrapper>
        )}
      </StyledExpiryCVVWrapper>
    </>
  );
};

export default FirstDataCreditCardInputs;
